/**
 * executionPolicy.js — Deterministic execution policy resolver (FASE 19.6)
 *
 * Maps each tool + user context to one of three policy levels:
 *
 *  SAFE                — execute immediately (navigation, view-only)
 *  CONFIRMATION_REQUIRED — show explicit approval UI before acting
 *  BLOCKED             — not available for this user/context/risk level
 *
 * This is the gate between "Kevia suggests X" and "X actually happens".
 * Nothing executes without clearing this policy check.
 */

import { TOOL_RISK } from "./toolContracts";

export const POLICIES = Object.freeze({
  SAFE:                  "SAFE",
  CONFIRMATION_REQUIRED: "CONFIRMATION_REQUIRED",
  BLOCKED:               "BLOCKED",
});

/**
 * Resolves the execution policy for a tool + user role.
 *
 * @param {ToolContract} tool
 * @param {string}       userRole — "*" | "admin" | "coordinator" | "pastor"
 * @returns {"SAFE"|"CONFIRMATION_REQUIRED"|"BLOCKED"}
 */
export function resolvePolicy(tool, userRole = "*") {
  if (!tool) return POLICIES.BLOCKED;

  // High-risk tools are always blocked at policy level
  if (tool.riskLevel === TOOL_RISK.HIGH) return POLICIES.BLOCKED;

  // Role access check
  const allowed =
    tool.allowedRoles.includes("*") || tool.allowedRoles.includes(userRole);
  if (!allowed) return POLICIES.BLOCKED;

  // Mutative tools require confirmation regardless of risk level
  if (tool.requiresConfirmation) return POLICIES.CONFIRMATION_REQUIRED;

  return POLICIES.SAFE;
}

/**
 * Returns a human-readable reason when a tool is BLOCKED.
 * Returns null when the policy is not BLOCKED.
 *
 * @param {ToolContract} tool
 * @param {string}       userRole
 * @returns {string|null}
 */
export function getPolicyReason(tool, userRole) {
  if (!tool) return "Herramienta no disponible";
  if (tool.riskLevel === TOOL_RISK.HIGH) return "Acción de alto riesgo — debe realizarse manualmente";
  const allowed = tool.allowedRoles.includes("*") || tool.allowedRoles.includes(userRole);
  if (!allowed) return "No tienes permisos para esta acción";
  return null;
}
